import { RecordingObjectType } from "../../../enums";
import { TypeOrRecord } from "../../TypeOrRecord";
import { getColorForUserName } from "../../../utils/getColorForUserName";
import {
  Avatar,
  Card,
  Header,
  Info,
  Name,
  Time,
  Text,
  Kebab,
  Dropdown,
  DropdownItem,
  Row
} from "./comment-card.style";
import { useEffect, useMemo, useRef, useState } from "react";
import { getFriendlyTimeAgo } from "../../../utils/time";
import { AnimatePresence } from "framer-motion";
import { useAccount } from "../../../hooks/use-account";
import { useCollaborative, Comment } from "../use-collaborative";

interface ICommentCardProps {
  comment: Comment;
  canComment?: boolean;
  /** Show the resolve/reopen control (building blocks only — topics leaves it off). */
  canResolve?: boolean;
}

export const CommentCard = ({
  comment,
  canComment = true,
  canResolve = false
}: ICommentCardProps) => {
  const {
    setActiveComment,
    updateComment,
    resolveComment,
    setCommentsInEditMode,
    commentsInEditMode,

    deleteComments
  } = useCollaborative();
  const { vojomoAccount } = useAccount();

  const [showMenu, setShowMenu] = useState(false);

  const menuRef = useRef<HTMLDivElement>(null);

  const isEditMode = useMemo(() => {
    return commentsInEditMode.includes(comment.id);
  }, [commentsInEditMode]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowMenu(false);
      }
    };

    if (showMenu) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showMenu]);

  return (
    <Card data-comment-card-id={comment.id}>
      <Row>
        <Avatar bg={getColorForUserName(comment.owner.name)}>
          {comment.owner.name
            .split(" ")
            .map((n) => n[0])
            .join("")}
        </Avatar>
        <Info>
          <Header>
            <Name>{comment.owner.name}</Name>
          </Header>
          <Time>{getFriendlyTimeAgo(comment.updatedAt)}</Time>
        </Info>
        {canResolve && !comment.parentComment && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              resolveComment(comment.id, !comment.isResolved);
            }}
            title={comment.isResolved ? "Reopen this thread" : "Mark this thread resolved"}
            style={{
              border: 0,
              background: "transparent",
              cursor: "pointer",
              fontSize: 11,
              fontWeight: 600,
              padding: "2px 6px",
              borderRadius: 6,
              whiteSpace: "nowrap",
              color: comment.isResolved ? "#059669" : "#64748b"
            }}
          >
            {comment.isResolved ? "✓ Resolved" : "Resolve"}
          </button>
        )}
        {canComment && comment.owner["id"] === vojomoAccount["_id"] && (
          <div ref={menuRef}>
            <Kebab
              className="kebab"
              onClick={(e) => {
                setShowMenu((prev) => !prev);
                e.stopPropagation();
              }}
            >
              ⋮
            </Kebab>

            <AnimatePresence>
              {showMenu && (
                <Dropdown
                  initial={{ opacity: 0, y: -4 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -4 }}
                  transition={{ duration: 0.15 }}
                >
                  <DropdownItem
                    onClick={(e) => {
                      e.stopPropagation();
                      setCommentsInEditMode((prev) => [...prev, comment.id]);
                      setShowMenu(false);
                      setActiveComment(null);
                    }}
                  >
                    Edit
                  </DropdownItem>
                  <DropdownItem
                    onClick={(e) => {
                      e.stopPropagation();

                      setActiveComment(null);
                      deleteComments([comment.id]);
                    }}
                  >
                    Delete
                  </DropdownItem>
                </Dropdown>
              )}
            </AnimatePresence>
          </div>
        )}
      </Row>
      {isEditMode ? (
        <TypeOrRecord
          recordingObjectType={RecordingObjectType.OUTPUT_COMMENT}
          onProcess={async (transcript: string, _: string) => {
            setActiveComment(null);
            updateComment(comment.id, transcript);

            setCommentsInEditMode((prev) => {
              const newArray = [...prev];
              return newArray.filter((c) => c !== comment.id);
            });
          }}
          defaultText={comment.text}
          autoFocus={true}
          placeHolder={""}
          submitButtonText={"Save"}
          clearTextOnSubmit={false}
          onlyType={!!comment.text}
        />
      ) : (
        <Text style={comment.isResolved ? { opacity: 0.55 } : undefined}>{comment.text}</Text>
      )}
    </Card>
  );
};
